import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Session } from '@providers/session/session.service';

/**
 * Response from /avatar/notification/should-show endpoint.
 */
export interface ShouldShowResponse {
  success: boolean;
  data: {
    shouldShow: boolean;
    reason: string;
  };
}

/**
 * Response from /avatar/notification/interaction endpoint.
 */
export interface InteractionResponse {
  success: boolean;
  data: {
    id: string;
    profileId: string;
    action: string;
    actionDate: string;
    createdAt: string;
  };
}

/**
 * Service for avatar notification modal API calls.
 * Communicates with avatar microservice at environment.API_AVATAR.
 *
 * @exports
 * @class AvatarNotificationService
 */
@Injectable()
export class AvatarNotificationService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(Session);

  /**
   * Get HTTP headers with JWT token for authentication.
   *
   * @private
   * @returns {object} HTTP headers with Authorization bearer token.
   * @memberof AvatarNotificationService
   */
  get headers(): Record<string, unknown> {
    return {
      headers: {
        Authorization: `Bearer ${this.session.token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  /**
   * Check if avatar notification modal should be shown to the user.
   * GET /avatar/notification/should-show.
   *
   * @returns {Observable<ShouldShowResponse>} Observable with should show status.
   * @memberof AvatarNotificationService
   */
  checkShouldShow(): Observable<ShouldShowResponse> {
    return this.http.get<ShouldShowResponse>(
      `${environment.API_AVATAR}/avatar/notification/should-show`,
      this.headers
    );
  }

  /**
   * Record user interaction with avatar notification modal.
   * POST /avatar/notification/interaction.
   *
   * @param {string} action - The action taken by user (SHOWN, STARTED, REMIND_LATER, SKIP, CLOSE_LATER, CLOSE_SKIP).
   * @returns {Observable<InteractionResponse>} Observable with interaction record.
   * @memberof AvatarNotificationService
   */
  recordInteraction(action: string): Observable<InteractionResponse> {
    return this.http.post<InteractionResponse>(
      `${environment.API_AVATAR}/avatar/notification/interaction`,
      { action },
      this.headers
    );
  }
}
